import React, { Component } from 'react';
import { AnnotatorObject, FeedbackSummaryArea as FSA } from 'annotator/annotator-types';
import FeedbackSummaryArea from 'annotator/feedback-summary-area';

declare const Annotator: AnnotatorObject;
declare const _;
declare const $;

interface FeedbackSummaryAreasProps {
  feedbackSummaryAreas: FSA[]
}

interface FeedbackSummaryAreasState {
  feedbackSummaryAreas: FSA[]
}

class FeedbackSummaryAreas extends Component<FeedbackSummaryAreasProps, FeedbackSummaryAreasState> {
  state = {
    feedbackSummaryAreas: this.adjustedFeedbackSummaryAreas()
  };

  constructor(props) {
    super(props);

    this.deleteCommentHelper = this.deleteCommentHelper.bind(this);
  }

  componentDidMount() {
    Annotator.addCommentHelperToFeedbackSummary = (commentHelper) => this.addCommentHelper(commentHelper);

    // Add any comment helpers that needed to be added before this was loaded
    // This can be removed when data is handled in a redux store that can be ready immediately on page load
    (Annotator.commentHelpersForFeedbackSummary || []).forEach((commentHelper) => this.addCommentHelper(commentHelper));

    // Check to enable return button after all editors have had time to render
    setTimeout(() => Annotator.checkIfReviewFinished(), 500);
  }

  componentWillUnmount() {
    Annotator.addCommentHelperToFeedbackSummary = undefined;
  }

  // Updates in the form of an object with values to assign to keys
  setCommentHelperProperties(commentHelper, updates) {
    const { feedbackSummaryAreas } = this.state;
    const { id, category } = commentHelper;
    const areaForCategory = feedbackSummaryAreas.find((fsa) => fsa.category === category);
    if (!areaForCategory) {
      return;
    }
    const { commentHelpers } = areaForCategory;
    const commentHelpersClone = commentHelpers.map((ch) => ((ch.id === id) ? { ...ch, ...updates } : ch));
    const areaForCategoryClone = { ...areaForCategory, commentHelpers: commentHelpersClone };
    const feedbackSummaryAreasClone =
      feedbackSummaryAreas.map((fsa) => (fsa.category === category ? areaForCategoryClone : fsa));

    this.setState({ feedbackSummaryAreas: feedbackSummaryAreasClone });
  }

  adjustedFeedbackSummaryAreas(): FSA[] {
    const { feedbackSummaryAreas } = this.props;

    return feedbackSummaryAreas.map((fsa) => {
      const commentHelperTimestamps = {};

      fsa.comment_helpers_feedback_summary_areas.forEach((chfsa) => {
        const { comment_helper_id: commentHelperId, created_at: createdAt } = chfsa;
        commentHelperTimestamps[commentHelperId] = createdAt;
      });

      const commentHelpers = fsa.comment_helpers.map((ch) => {
        const commentHelperClone = _.clone(ch);

        commentHelperClone.timestamp = commentHelperTimestamps[ch.id];

        return commentHelperClone;
      });

      const feedbackSummaryAreaClone = _.clone(fsa);
      feedbackSummaryAreaClone.commentHelpers = commentHelpers;
      feedbackSummaryAreaClone.comment_helpers = undefined;
      feedbackSummaryAreaClone.comment_helpers_feedback_summary_areas = undefined;

      return feedbackSummaryAreaClone;
    });
  }

  addCommentHelper(commentHelper) {
    this.addCommentHelperInBrowser(commentHelper).then(() => this.addCommentHelperOnServer(commentHelper));
  }

  addCommentHelperInBrowser(commentHelper) {
    return new Promise<void>((resolve) => {
      // Only include comment helpers that have a summary
      const { id, category, summary } = commentHelper;
      if (!summary?.length) return;

      const { clone } = _;
      const { feedbackSummaryAreas } = this.state;

      const areaForCategory = feedbackSummaryAreas.find((fsa) => fsa.category === category);
      if (!areaForCategory) return;

      const { commentHelpers } = areaForCategory;

      // check if it is already there
      if (commentHelpers.find((ch) => ch.id === id)) return;

      const commentHelpersClone = clone(commentHelpers);
      const commentHelperClone = clone(commentHelper);
      commentHelperClone.saved = false;
      commentHelpersClone.push(commentHelperClone);

      const areaForCategoryClone = clone(areaForCategory);
      areaForCategoryClone.commentHelpers = commentHelpersClone;

      const feedbackSummaryAreasClone =
        feedbackSummaryAreas.map((fsa) => (fsa.category === category ? areaForCategoryClone : fsa));

      this.setState({ feedbackSummaryAreas: feedbackSummaryAreasClone }, resolve);
    });
  }

  addCommentHelperOnServer(commentHelper) {
    if (Annotator.sandbox) {
      this.commentHelperSaved(commentHelper, { timestamp: Date.now() });
      return;
    }

    $.ajax({
      type: 'PUT',
      url: Annotator.routes.addFeedbackSummaryCommentHelper,
      data: {
        submission_uuid: Annotator.data.submissionUUID,
        comment_helper_id: commentHelper.id
      },
      success: ({ data }) => this.commentHelperSaved(commentHelper, data),
      error: () => this.commentHelperSaveError(commentHelper)
    });
  }

  commentHelperSaved(savedCommentHelper, { timestamp }) {
    window.Annotator.checkIfReviewFinished();
    this.setCommentHelperProperties(savedCommentHelper, { saved: true, timestamp });
  }

  commentHelperSaveError(commentHelper) {
    this.setCommentHelperProperties(commentHelper, { hasError: true });
  }

  deleteCommentHelper(commentHelper) {
    const { id } = commentHelper;

    this.setCommentHelperProperties(commentHelper, { deleting: true });

    if (Annotator.sandbox) {
      this.commentHelperDeleted(commentHelper);
      return;
    }

    $.ajax({
      type: 'DELETE',
      url: Annotator.routes.removeFeedbackSummaryCommentHelper,
      data: {
        submission_uuid: Annotator.data.submissionUUID,
        comment_helper_id: id
      },
      success: () => this.commentHelperDeleted(commentHelper),
      error: () => this.commentHelperDeleteError(commentHelper)
    });
  }

  commentHelperDeleted(deletedCommentHelper) {
    window.Annotator.checkIfReviewFinished();

    // remove the comment helper locally
    const { feedbackSummaryAreas } = this.state;
    const { id, category } = deletedCommentHelper;
    const areaForCategory = feedbackSummaryAreas.find((fsa) => fsa.category === category);
    if (!areaForCategory) {
      return;
    }
    const { commentHelpers } = areaForCategory;
    const commentHelpersClone = commentHelpers.filter((ch) => ch.id !== id);
    const areaForCategoryClone = { ...areaForCategory, commentHelpers: commentHelpersClone };
    const feedbackSummaryAreasClone =
      feedbackSummaryAreas.map((fsa) => (fsa.category === category ? areaForCategoryClone : fsa));

    this.setState({ feedbackSummaryAreas: feedbackSummaryAreasClone });
  }

  commentHelperDeleteError(commentHelper) {
    this.setCommentHelperProperties(commentHelper, { hasError: true });
  }

  render() {
    const { feedbackSummaryAreas } = this.state;

    return (
      <div className="d-flex flex-column gap-2 py-3 px-1">
        {feedbackSummaryAreas.map(({
          category, category_name: categoryName, content, commentHelpers
        }) => (
          <FeedbackSummaryArea
            key={category}
            category={category}
            categoryName={categoryName}
            content={content}
            commentHelpers={commentHelpers}
            deleteCommentHelper={this.deleteCommentHelper}
          />
        ))}
      </div>
    );
  }
}

export default FeedbackSummaryAreas;
